"use client"
import styled from "@emotion/styled"
import { sharedColors } from "@eurodrive/ui/theme/colors"
import { Typography } from "@mui/material"
import { useRef } from "react"
import { dealTypeOptions } from "../Search"
import { DealType } from "./useSearchFilters"

const StyledSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const StyledRadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const StyledRadioOption = styled.div<{ checked: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
`

const StyledRadioText = styled(Typography)<{ checked: boolean }>`
  font-size: 16px;
  line-height: 140%;
  color: ${sharedColors.black};
  font-weight: ${(props) => (props.checked ? 600 : 400)};
`

const StyledRadioIcon = styled.img`
  width: 24px;
  height: 24px;
`

interface DealTypeSectionProps {
  dealType: DealType
  handleDealTypeChange: (type: DealType) => void
}

function DealTypeSection({
  dealType,
  handleDealTypeChange,
}: DealTypeSectionProps) {
  const radioRefs = useRef<(HTMLDivElement | null)[]>([])

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLDivElement>,
    index: number,
  ) => {
    const optionsCount = dealTypeOptions.length
    let newIndex: number | null = null

    switch (e.key) {
      case "ArrowDown":
      case "ArrowRight":
        e.preventDefault()
        newIndex = (index + 1) % optionsCount
        break
      case "ArrowUp":
      case "ArrowLeft":
        e.preventDefault()
        newIndex = (index - 1 + optionsCount) % optionsCount
        break
      case "Home":
        e.preventDefault()
        newIndex = 0
        break
      case "End":
        e.preventDefault()
        newIndex = optionsCount - 1
        break
      case " ":
      case "Enter":
        e.preventDefault()
        handleDealTypeChange(dealTypeOptions[index].value)
        return
      default:
        return
    }

    if (newIndex !== null) {
      radioRefs.current[newIndex]?.focus()
      handleDealTypeChange(dealTypeOptions[newIndex].value)
    }
  }

  const currentIndex = dealTypeOptions.findIndex(
    (option) => option.value === dealType,
  )

  return (
    <StyledSection>
      <Typography variant="body2" fontWeight={600} id="deal-type-label">
        סוג רכב
      </Typography>
      <StyledRadioGroup role="radiogroup" aria-labelledby="deal-type-label">
        {dealTypeOptions.map((option, index) => {
          const isChecked = dealType === option.value
          return (
            <StyledRadioOption
              key={option.value}
              ref={(el) => {
                radioRefs.current[index] = el
              }}
              checked={isChecked}
              onClick={() => handleDealTypeChange(option.value)}
              role="radio"
              aria-checked={isChecked}
              tabIndex={index === currentIndex ? 0 : -1}
              onKeyDown={(e) => handleKeyDown(e, index)}
            >
              <StyledRadioIcon
                src={
                  isChecked
                    ? "/icons/radio-checked.svg"
                    : "/icons/radio-unchecked.svg"
                }
                alt=""
                aria-hidden="true"
              />
              <StyledRadioText checked={isChecked}>
                {option.label}
              </StyledRadioText>
            </StyledRadioOption>
          )
        })}
      </StyledRadioGroup>
    </StyledSection>
  )
}

export default DealTypeSection
